<template>
    <div v-if="visible" class="rfd-overlay" @click.self="onCancel">
        <div class="rfd-modal" role="dialog" aria-modal="true">
            <!-- Header -->
            <div class="rfd-header">
                <h2 class="rfd-title">{{ title }}</h2>
                <button type="button" class="rfd-close" aria-label="close" @click="onCancel">
                    <X :size="20" color="#ffffff" />
                </button>
            </div>

            <!-- Icon + message -->
            <div class="rfd-body">
                <div class="rfd-icon" :class="'rfd-icon--' + variant">
                    <CheckCircle2 v-if="variant === 'success'" :size="26" />
                    <AlertTriangle v-else-if="variant === 'danger'" :size="26" />
                    <Info v-else :size="26" />
                </div>
                <p v-if="message" class="rfd-message">{{ message }}</p>
                <slot />
            </div>

            <!-- Footer -->
            <div class="rfd-footer">
                <button v-if="!hideCancel" type="button" class="rfd-btn rfd-btn--ghost" @click="onCancel">
                    {{ cancelText || $t('common.cancel') }}
                </button>
                <button
                    type="button"
                    class="rfd-btn"
                    :class="variant === 'danger' ? 'rfd-btn--danger' : 'rfd-btn--primary'"
                    :disabled="loading"
                    @click="onConfirm"
                >
                    <span v-if="loading" class="rfd-spinner"></span>
                    <span>{{ confirmText || $t('invoices.ok') }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { X, CheckCircle2, AlertTriangle, Info } from 'lucide-vue'

/**
 * Dark refund-flow dialog — shares the look of CreateCreditNoteModal (dark #1a1438 surface,
 * purple gradient primary button, round close button) so the cancel-confirmation and the
 * success/error feedback steps of the credit-note flow feel cohesive with the avoir modal.
 *
 * Deliberately NOT the shared Shared/ConfirmationDialog.vue (used in many light-themed screens);
 * this is scoped to the refund flow on the commande page.
 */
export default {
    name: 'RefundFlowDialog',
    components: { X, CheckCircle2, AlertTriangle, Info },
    props: {
        value: { type: Boolean, default: false },
        title: { type: String, default: '' },
        message: { type: String, default: '' },
        confirmText: { type: String, default: '' },
        cancelText: { type: String, default: '' },
        hideCancel: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        // 'default' | 'success' | 'danger' — drives the icon + primary button colour.
        variant: { type: String, default: 'default' },
    },
    computed: {
        visible: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
    },
    methods: {
        onConfirm() {
            this.$emit('confirm')
        },
        onCancel() {
            this.visible = false
            this.$emit('cancel')
        },
    },
}
</script>

<style lang="scss" scoped>
.rfd-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background-color: rgba(2, 6, 23, 0.85);
    overflow-y: auto;
}

.rfd-modal {
    position: relative;
    width: 100%;
    max-width: 460px;
    margin: auto;
    padding: 28px;
    background-color: #1a1438;
    border: 1px solid #3b2f6b;
    border-radius: 22px;
    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.55);
    color: #f1f5f9;
    font-family: inherit;
}

.rfd-header {
    position: relative;
    display: flex;
    align-items: center;
    margin-bottom: 8px;
}

.rfd-title {
    margin: 0;
    font-size: 19px;
    font-weight: 700;
    color: #ffffff;
    padding-right: 44px;
}

.rfd-close {
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border: none;
    border-radius: 9999px;
    background-color: #2b2356;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: #3a3070;
    }
}

.rfd-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 18px 4px 26px;
}

.rfd-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 9999px;
    margin-bottom: 16px;

    &--success {
        background-color: rgba(34, 197, 94, 0.15);
        color: #4ade80;
    }

    &--danger {
        background-color: rgba(239, 68, 68, 0.15);
        color: #f87171;
    }

    &--default {
        background-color: rgba(168, 85, 247, 0.15);
        color: #c084fc;
    }
}

.rfd-message {
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
    color: #cbd5e1;
}

.rfd-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
}

.rfd-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 48px;
    padding: 0 24px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition:
        opacity 0.15s ease,
        background-color 0.15s ease;

    &--ghost {
        background-color: transparent;
        border: 1px solid #3b2f6b;
        color: #ffffff;

        &:hover {
            background-color: rgba(255, 255, 255, 0.05);
        }
    }

    &--primary {
        border: none;
        color: #ffffff;
        background: linear-gradient(90deg, #7c3aed 0%, #4f46e5 100%);

        &:hover:not(:disabled) {
            opacity: 0.92;
        }
    }

    &--danger {
        border: none;
        color: #ffffff;
        background: linear-gradient(90deg, #ef4444 0%, #b91c1c 100%);

        &:hover:not(:disabled) {
            opacity: 0.92;
        }
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.rfd-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #ffffff;
    border-radius: 9999px;
    animation: rfd-spin 0.6s linear infinite;
}

@keyframes rfd-spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 480px) {
    .rfd-modal {
        padding: 20px;
    }

    .rfd-footer {
        flex-direction: column-reverse;
        align-items: stretch;
    }
}
</style>
